
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft, FiCopy as Copy, FiCheck as Check, FiCode as Code, FiLink as LinkIcon,
  FiAlertCircle as AlertCircle, FiClock as Clock, FiDollarSign as DollarSign, FiActivity as Activity, FiHash as Hash, FiZap as Zap, FiSave as Save,
  FiChevronDown as ChevronDown, FiChevronRight as ChevronRight, FiCpu as Cpu
} from 'react-icons/fi';
import { getTrace, getTraceGenerations } from '../../../../services/aiOperationsApi';
import SavePromptModal from '../../../../components/SavePromptModal';

interface Generation {
  id: string;
  model_id: string;
  model_provider: string;
  prompt?: string;
  completion?: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cost: number;
  latency_ms?: number;
  status: string;
  timestamp?: string;
  step_id?: string;
}

interface TraceData {
  id: string;
  status: string;
  prompt?: string;
  completion?: string;
  metadata_json?: string;
  model_id?: string;
  model_provider?: string;
  agent_name?: string;
  input_tokens?: number;
  output_tokens?: number;
  cost?: number;
  duration_seconds?: number;
  latency_ms?: number;
  start_time?: string;
  timestamp?: string;
  created_at?: string;
  run_id?: string;
  step_id?: string;
}

const TraceDetailPage: React.FC = () => {
  const { traceId } = useParams();
  const navigate = useNavigate();
  const [trace, setTrace] = useState<TraceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('metadata');
  const [showSavePromptModal, setShowSavePromptModal] = useState(false);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [generationsLoading, setGenerationsLoading] = useState(false);
  const [generationsLoaded, setGenerationsLoaded] = useState(false);
  const [expandedGeneration, setExpandedGeneration] = useState<string | null>(null);

  useEffect(() => {
    loadTrace();
  }, [traceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrace = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTrace(traceId!);
      setTrace(data);
    } catch (err: unknown) {
      console.error('Error loading trace:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trace');
    } finally {
      setLoading(false);
    }
  };

  const loadGenerations = async () => {
    if (generationsLoaded || generationsLoading) return;
    setGenerationsLoading(true);
    try {
      const data = await getTraceGenerations(traceId!);
      setGenerations(data.generations || []);
      if (data.generations?.length > 0) {
        setExpandedGeneration(data.generations[0].id);
      }
    } catch (err) {
      console.error('Error loading generations:', err);
    } finally {
      setGenerationsLoading(false);
      setGenerationsLoaded(true);
    }
  };

  const handleGenerationsTab = () => {
    setActiveTab('generations');
    loadGenerations();
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleBack = () => {
    navigate('/ai-operations/observability?tab=traces');
  };

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms: number | undefined) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const isAggregatedTracking = () => {
    try {
      const metadata = trace?.metadata_json ? JSON.parse(trace.metadata_json) : null;
      return metadata?.tracking_type === 'aggregated';
    } catch {
      return false;
    }
  };

  const getMetadata = () => {
    try {
      return trace?.metadata_json ? JSON.parse(trace.metadata_json) : {};
    } catch {
      return {};
    }
  };

  const getModelInfo = () => {
    // Try to get model from metadata_json first
    const metadata = getMetadata();
    if (metadata.model_id || metadata.model) {
      return {
        model: metadata.model_id || metadata.model,
        provider: metadata.model_provider || metadata.provider
      };
    }
    // Fallback to trace fields
    return {
      model: trace?.model_id || trace?.agent_name || 'Unknown',
      provider: trace?.model_provider
    };
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!trace) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Trace not found
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Traces
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Trace Details
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <code className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                {trace.id}
              </code>
              <button
                onClick={() => handleCopy(trace.id, 'id')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {copiedField === 'id' ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              trace.status === 'completed'
                ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
            }`}>
              {trace.status}
            </span>
            {isAggregatedTracking() && (
              <span className="px-3 py-1 text-sm font-medium rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300">
                Aggregated
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Model & Provider */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Model / Agent</div>
            <div className="flex items-center">
              <Code className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {getModelInfo().model}
              </span>
            </div>
            {getModelInfo().provider && (
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Provider: {getModelInfo().provider}
              </div>
            )}
          </div>

          {/* Tokens */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tokens</div>
            <div className="flex items-center">
              <Hash className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {trace.input_tokens?.toLocaleString() || 0}↓ / {trace.output_tokens?.toLocaleString() || 0}↑
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Total: {((trace.input_tokens || 0) + (trace.output_tokens || 0)).toLocaleString()}
            </div>
          </div>

          {/* Cost */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cost</div>
            <div className="flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                ${Number(trace.cost || 0).toFixed(4)}
              </span>
            </div>
          </div>

          {/* Latency */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Latency</div>
            <div className="flex items-center">
              <Zap className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {formatDuration((trace.duration_seconds || 0) * 1000 || trace.latency_ms)}
              </span>
            </div>
          </div>

          {/* Timestamp */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Timestamp</div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {formatTimestamp(trace.start_time || trace.timestamp || trace.created_at)}
              </span>
            </div>
          </div>

          {/* Created At */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Created</div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {formatTimestamp(trace.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Prompt Section */}
      {trace.prompt && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Input Prompt
            </h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => { if (trace.prompt) handleCopy(trace.prompt, 'prompt'); }}
                className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                {copiedField === 'prompt' ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={() => setShowSavePromptModal(true)}
                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save className="w-4 h-4 mr-1" />
                Save to Library
              </button>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
              {trace.prompt}
            </pre>
          </div>
        </div>
      )}

      {/* Completion Section */}
      {trace.completion && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Model Response
            </h2>
            <button
              onClick={() => { if (trace.completion) handleCopy(trace.completion, 'completion'); }}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              {copiedField === 'completion' ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </button>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
              {trace.completion}
            </pre>
          </div>
        </div>
      )}

      {/* Related Information Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-4 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('metadata')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'metadata'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Metadata
            </button>
            <button
              onClick={handleGenerationsTab}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'generations'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              LLM Generations
            </button>
            {trace.run_id && (
              <button
                onClick={() => setActiveTab('run')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'run'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Parent Run
              </button>
            )}
            {trace.step_id && (
              <button
                onClick={() => setActiveTab('step')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'step'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Step Context
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Trace Metadata
              </h3>
              {trace.metadata_json ? (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-gray-800 dark:text-gray-200 font-mono">
                    {JSON.stringify(getMetadata(), null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-400 text-center py-8">
                  No metadata available
                </div>
              )}
            </div>
          )}

          {/* LLM Generations Tab */}
          {activeTab === 'generations' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                LLM Generations
                {generations.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({generations.length} call{generations.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h3>

              {generationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : generations.length === 0 ? (
                <div className="text-gray-600 dark:text-gray-400 text-center py-8">
                  No LLM generations found for this trace
                </div>
              ) : (
                <div className="space-y-3">
                  {generations.map((gen, index) => {
                    const isExpanded = expandedGeneration === gen.id;
                    return (
                      <div key={gen.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        {/* Accordion Header */}
                        <button
                          onClick={() => setExpandedGeneration(isExpanded ? null : gen.id)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <Cpu className="w-4 h-4 text-indigo-500" />
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                              #{index + 1} — {gen.model_id}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              gen.status === 'completed'
                                ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                            }`}>
                              {gen.status}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span>{gen.input_tokens + gen.output_tokens} tokens</span>
                            <span>${gen.cost.toFixed(4)}</span>
                            {gen.latency_ms && <span>{gen.latency_ms}ms</span>}
                          </div>
                        </button>

                        {/* Accordion Content */}
                        {isExpanded && (
                          <div className="px-4 py-4 space-y-4">
                            {/* Metrics Row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Input</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{gen.input_tokens.toLocaleString()}</div>
                              </div>
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Output</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{gen.output_tokens.toLocaleString()}</div>
                              </div>
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Cache Read</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{gen.cache_read_tokens.toLocaleString()}</div>
                              </div>
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Cache Write</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{gen.cache_write_tokens.toLocaleString()}</div>
                              </div>
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Cost</div>
                                <div className="text-sm font-medium text-green-600 dark:text-green-400">${gen.cost.toFixed(4)}</div>
                              </div>
                              <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Latency</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{gen.latency_ms ? `${gen.latency_ms}ms` : 'N/A'}</div>
                              </div>
                            </div>

                            {/* Provider & Timestamp */}
                            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                              <span>Provider: <strong className="text-gray-900 dark:text-white">{gen.model_provider}</strong></span>
                              {gen.timestamp && (
                                <span>Time: <strong className="text-gray-900 dark:text-white">{new Date(gen.timestamp).toLocaleString()}</strong></span>
                              )}
                            </div>

                            {/* Prompt */}
                            {gen.prompt && (
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Input Prompt</h4>
                                  <button
                                    onClick={() => handleCopy(gen.prompt!, `gen-prompt-${gen.id}`)}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 flex items-center"
                                  >
                                    {copiedField === `gen-prompt-${gen.id}` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                                    {copiedField === `gen-prompt-${gen.id}` ? 'Copied' : 'Copy'}
                                  </button>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto">
                                  <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">{gen.prompt}</pre>
                                </div>
                              </div>
                            )}

                            {/* Completion */}
                            {gen.completion && (
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Model Response</h4>
                                  <button
                                    onClick={() => handleCopy(gen.completion!, `gen-completion-${gen.id}`)}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 flex items-center"
                                  >
                                    {copiedField === `gen-completion-${gen.id}` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                                    {copiedField === `gen-completion-${gen.id}` ? 'Copied' : 'Copy'}
                                  </button>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto">
                                  <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">{gen.completion}</pre>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Parent Run Tab */}
          {activeTab === 'run' && trace.run_id && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Parent Agent Run
              </h3>
              <div className="flex items-center space-x-4">
                <LinkIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Run ID</div>
                  <code className="text-sm text-gray-900 dark:text-white font-mono">
                    {trace.run_id}
                  </code>
                </div>
                <button
                  onClick={() => navigate(`/ai-operations/runs/${trace.run_id}`)}
                  className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Run
                </button>
              </div>
            </div>
          )}

          {/* Step Context Tab */}
          {activeTab === 'step' && trace.step_id && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Agent Step Context
              </h3>
              <div className="flex items-center space-x-4">
                <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Step ID</div>
                  <code className="text-sm text-gray-900 dark:text-white font-mono">
                    {trace.step_id}
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Section (if failed) */}
      {trace.status === 'failed' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-300 mb-2">
                Execution Failed
              </h3>
              <p className="text-red-800 dark:text-red-400">
                This trace failed during execution. Check the metadata or parent run for more details.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save Prompt Modal */}
      <SavePromptModal
        isOpen={showSavePromptModal}
        onClose={() => setShowSavePromptModal(false)}
        promptText={trace?.prompt || ''}
        metadata={{
          trace_id: trace?.id,
          model_id: trace?.model_id,
          model_provider: trace?.model_provider,
          run_id: trace?.run_id
        }}
      />
    </div>
  );
};

export default TraceDetailPage;
